/**
 * Friends, drawn to the reference.
 *
 * "Friends / Better together" with Add friend; one panel holding All, Online,
 * Requests and Blocked with search beside them; each friend a row of its own
 * with what they are doing, Message, Invite and the options button. Resting
 * on a friend shows a preview - their banner, bio, games and languages - with
 * View profile. Beside it (from 1280px, below it under that) friend requests
 * as cards with Accept and Decline, people you may know, and the pug.
 *
 * Every line under a name is something that is true: the game the desktop app
 * saw running, the party they are in, their status, or when they were last
 * online. Nothing is invented to fill the row. Rows lay themselves out by the
 * room the panel really has, down to a 615px window.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, endpoints, socket } from '@/lib/api';
import type { Friendship, FriendKind, Party, PublicUser } from '@/lib/types';
import { go, useRoute } from '@/lib/hash';
import { useSession } from '@/stores/session';
import * as Icons from '@/icons';
import { UserName, openUserCard } from '@/components/user';
import { Avatar } from '@/components/Avatar';
import { Icon } from '@/icons/Icon';
import { Button, Empty, IconButton, Modal, useToast } from '@/components/ui';
import { InviteToParty, useUserMenu } from '@/components/userMenu';
import { Teammates } from '@/components/Teammates';
import { RecentSessions } from '@/components/Sessions';
import { FriendListBar, useFriendLists } from '@/components/FriendLists';
import { AvailableTag } from '@/components/Availability';
import { ago } from '@/lib/notifications';
import { inTextField } from '@/lib/keybinds';
import { useMinWidth } from '@/lib/viewport';
import { cn } from '@/lib/cn';
import { usePaged, ShowMore } from '@/components/Paged';

type Tab = 'all' | 'online' | 'requests' | 'blocked';
type Action = { path: string; body?: unknown };
type Person = { user: PublicUser; mutuals: number; games: string[] };

const TABS: Tab[] = ['all', 'online', 'requests', 'blocked'];

/* Edges are inset lines, not borders, so rounded corners stay smooth at any scale. */
const PANEL = 'pugo-panel rounded-[calc(22px*var(--radius-mul,1))] bg-surface-1 shadow-[inset_0_0_0_1px_var(--line-soft)]';
/*
 * A row inside a panel is a row.
 *
 * These were panels of their own - the scene material, the frame, the lot -
 * stacked inside a panel that already had all of it. In a theme that draws a
 * real edge, a list of twenty friends was twenty gold frames inside a
 * twenty-first, and none of them meant anything. The crew list has always
 * been one frame with plain rows in it, and that is the right shape: the
 * panel says "this is a list", the rows say nothing and are read instead.
 */
const ROW = 'rounded-[calc(14px*var(--radius-mul,1))] transition-[background-color] duration-[250ms] hover:bg-surface-2';

const isUp = (u: PublicUser) => u.status !== 'offline' && u.status !== 'invisible';

/** "3m ago", or "just now". */
function sinceText(t: number | undefined) {
  if (!t) return '';
  const a = ago(t);
  return /now/i.test(a) ? a : a + ' ago';
}

/** "3 hours ago", in words, for "Last online". */
function longAgo(t: number) {
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return 'just now';
  const unit = (n: number, w: string) => n + ' ' + w + (n === 1 ? '' : 's') + ' ago';
  if (s < 3600) return unit(Math.floor(s / 60), 'minute');
  if (s < 86400) return unit(Math.floor(s / 3600), 'hour');
  if (s < 604800) return unit(Math.floor(s / 86400), 'day');
  return new Date(t).toLocaleDateString();
}

export function FriendsView() {
  const routeQ = useRoute().query as Record<string, string | undefined>;
  const [tab, setTab] = useState<Tab>('all');
  useEffect(() => {
    // Missing means All, so going back to #/friends actually goes back.
    const next = routeQ.tab ?? 'all';
    if (next !== tab && (TABS as string[]).includes(next)) setTab(next as Tab);
  }, [routeQ.tab]);

  const [filter, setFilter] = useState('');
  /* Which of your own lists is being looked at, if any. */
  const [list, setList] = useState('');
  const friendLists = useFriendLists();
  const settings = useSession((s) => s.settings);
  const sort = settings?.sortFriends ?? 'online';
  const [adding, setAdding] = useState(false);
  /* #/friends?add=1 opens the panel - search and the empty states both use it
     as the one address that means "add somebody". */
  useEffect(() => { if (routeQ.add) setAdding(true); }, [routeQ.add]);
  const railBeside = useMinWidth(1280);
  const searchRef = useRef<HTMLInputElement>(null);

  const queryClient = useQueryClient();
  const toast = useToast();

  const { data, isPending, isError, refetch, isFetching } = useQuery({ queryKey: ['friends'], queryFn: endpoints.friends });

  /* Presence and friend changes both arrive on the socket; one invalidation
     keeps the list current without polling. */
  useEffect(() => {
    const refresh = () => queryClient.invalidateQueries({ queryKey: ['friends'] });
    const off = [
      socket.on('presence', refresh),
      socket.on('friend:request', refresh),
      socket.on('friend:accepted', refresh),
      socket.on('friend:removed', refresh),
      socket.on('user:updated', refresh)
    ];
    return () => off.forEach((fn) => fn());
  }, [queryClient]);

  /* "/" jumps to the search. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey || inTextField(e.target)) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /** Accept, decline, cancel, unblock - all the same shape of call. */
  const mutate = useMutation({
    mutationFn: ({ path, body }: Action) =>
      (path.startsWith('DELETE ') ? api.del(path.slice(7)) : api.post(path, body ?? {})),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['friends'] });
      void queryClient.invalidateQueries({ queryKey: ['friend-suggestions'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (err: Error) => toast(err.message, 'bad')
  });
  /* Which call is in flight, so only that row's buttons wait. */
  const busyPath = mutate.isPending ? mutate.variables?.path : undefined;

  const lists = {
    friends: data?.friends ?? [],
    incoming: data?.incoming ?? [],
    outgoing: data?.outgoing ?? [],
    blocked: data?.blocked ?? []
  };

  /* Suggestions, ranked on friends you genuinely share. */
  const suggestions = useQuery({
    queryKey: ['friend-suggestions'],
    queryFn: () => api.get<{ people: Person[] }>('/api/friends/suggestions')
  });
  const people = suggestions.data?.people ?? [];

  /* Which party each person is in, from the list Home and Games already share. */
  const parties = useQuery({
    queryKey: ['parties', 'all'],
    queryFn: () => endpoints.parties({ scope: 'all' }),
    staleTime: 15_000
  });
  const partyOf = useMemo(() => {
    const map = new Map<string, Party>();
    for (const p of parties.data?.parties ?? []) {
      if (p.closed) continue;
      for (const m of p.members ?? []) map.set(m.userId, p);
    }
    return map;
  }, [parties.data]);

  const online = lists.friends.filter((f) => isUp(f.user));

  function kindOf(f: Friendship): FriendKind {
    if (lists.incoming.some((x) => x.id === f.id)) return 'incoming';
    if (lists.outgoing.some((x) => x.id === f.id)) return 'outgoing';
    if (lists.blocked.some((x) => x.id === f.id)) return 'blocked';
    return 'accepted';
  }

  /* What the chosen tab is showing, filtered and ordered. */
  const shown = useMemo(() => {
    const base =
      tab === 'online' ? online
        : tab === 'requests' ? [...lists.incoming, ...lists.outgoing]
          : tab === 'blocked' ? lists.blocked
            : lists.friends;

    const q = filter.trim().toLowerCase();
    let matched = q
      ? base.filter((f) => (f.user.username + ' ' + f.user.displayName).toLowerCase().includes(q))
      : base;

    /* One of your own lists, if you are looking at one. */
    if (list) {
      const only = new Set(((friendLists.data?.lists ?? []).find((l) => l.name === list)?.members ?? []).map((u) => u.id));
      matched = matched.filter((f) => only.has(f.user.id));
    }

    const up = (f: Friendship) => (isUp(f.user) ? 0 : 1);
    return [...matched].sort((a, b) => {
      /* Pinned friends stay at the top whatever the sort - that is what
         pinning them was for. */
      const pin = (b.favorite ? 1 : 0) - (a.favorite ? 1 : 0);
      if (pin) return pin;
      if (sort === 'online') {
        const d = up(a) - up(b);
        if (d) return d;
      }
      if (sort === 'recent') return (b.since ?? 0) - (a.since ?? 0);
      return a.user.displayName.localeCompare(b.user.displayName);
    });
  }, [tab, filter, sort, list, friendLists.data, lists.friends, lists.incoming, lists.outgoing, lists.blocked, online]);

  const tabs: { id: Tab; label: string; count?: number; dot?: string }[] = [
    { id: 'all', label: 'All', count: lists.friends.length },
    { id: 'online', label: 'Online', count: online.length, dot: 'bg-good' },
    { id: 'requests', label: 'Requests', count: lists.incoming.length + lists.outgoing.length, dot: 'bg-warn' },
    { id: 'blocked', label: 'Blocked' }
  ];

  function onTabKey(e: React.KeyboardEvent) {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const i = TABS.indexOf(tab);
    const next = TABS[(i + (e.key === 'ArrowRight' ? 1 : TABS.length - 1)) % TABS.length]!;
    setTab(next);
    document.getElementById('friends-tab-' + next)?.focus();
  }

  /*
   * Nothing is remembered about what was sent.
   *
   * There used to be a list of ids here that only ever grew. Send a request
   * and cancel it and the row still said Sent, because the row was reading
   * that list rather than the friends list - and the friends list was right
   * the whole time. Now the send just refreshes it, which the mutation
   * already does, and the row reads the answer.
   */
  const addSuggested = (p: Person) =>
    mutate.mutate(
      { path: '/api/friends/request', body: { username: p.user.username } },
      { onSuccess: () => toast('Friend request sent to ' + p.user.displayName, 'good') }
    );

  /* Ten rows to start with. A new tab, a new search or a new sort is a
     different list, so each of those goes back to the first ten. */
  const paged = usePaged(shown, tab + '|' + filter + '|' + sort + '|' + list);

  const tabLabel = tabs.find((t) => t.id === tab)!.label;

  const rail = (
    <>
      <RequestsCard incoming={lists.incoming} busyPath={busyPath} onAction={(a) => mutate.mutate(a)} onViewAll={() => setTab('requests')} />
      <Teammates limit={4} />
      <RecentSessions limit={3} />
      {people.length ? <SuggestionsCard people={people.slice(0, 3)} outgoing={lists.outgoing} busy={mutate.isPending} onAdd={addSuggested} /> : null}
    </>
  );

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-[1440px] px-4 py-6 sm:px-6 xl:px-8">

        {/* ---- header ---- */}
        <header className="relative mb-6 flex flex-wrap items-center gap-x-6 gap-y-4 animate-[fade-in_280ms_ease-out] motion-reduce:animate-none">
          <div className="min-w-0">
            <p className="m-0 text-[12px] font-bold tracking-[.2em] text-mute uppercase">Friends</p>
            <h1 className="mt-1 mb-0 text-[32px] leading-[1.05] font-extrabold tracking-[-.035em] text-ink sm:text-[42px]">Better together</h1>
            <p className="mt-1.5 mb-0 text-[16px] text-mute">Play, chat and make gaming more fun with friends.</p>
          </div>
          <Button kind="primary" icon={<Icon name="addFriend" size={18} />} className="h-12 self-start rounded-[calc(14px*var(--radius-mul,1))] px-6 text-[16px] sm:mt-6" onClick={() => setAdding(true)}>
            Add friend
          </Button>
        </header>

        <div className="flex items-start gap-6">
          <section aria-label="Your friends" className={cn(PANEL, '@container min-w-0 flex-1 p-3 sm:p-5')}>
            {/* ---- tabs and search ---- */}
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <div role="tablist" aria-label="Friend lists" onKeyDown={onTabKey} className="no-scrollbar -my-2 -mx-1 flex max-w-full items-center gap-1.5 overflow-x-auto px-1 py-2">
                {tabs.map((t) => {
                  const on = t.id === tab;
                  return (
                    <button
                      key={t.id}
                      id={'friends-tab-' + t.id}
                      type="button"
                      role="tab"
                      aria-selected={on}
                      aria-controls="friends-panel"
                      tabIndex={on ? 0 : -1}
                      onClick={() => setTab(t.id)}
                      className={cn(
                        'flex h-11 flex-none items-center gap-2.5 rounded-[calc(14px*var(--radius-mul,1))] px-5 text-[15.5px] font-semibold outline-none',
                        'transition-[background-color,color,box-shadow] duration-[250ms] ease-[var(--ease-out)] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                        /* The same buttons as Notifications: the one you are on is filled
                           in the accent, the rest are outlined cards. */
                        on ? 'bg-accent text-accent-fg shadow-[0_8px_20px_-10px_color-mix(in_srgb,var(--accent)_80%,transparent)]' : 'bg-surface-1 text-ink shadow-[inset_0_0_0_1px_var(--line)] hover:bg-surface-2'
                      )}
                    >
                      {t.dot ? <span aria-hidden className={cn('h-2.5 w-2.5 rounded-full', t.dot, on && 'ring-2 ring-[color-mix(in_srgb,var(--accent-fg)_70%,transparent)]')} /> : null}
                      {t.label}
                      {t.count != null ? (
                        <span className={cn('grid h-6 min-w-6 place-items-center rounded-full px-1.5 text-[12.5px] font-bold tabular-nums', on ? 'bg-accent-fg text-accent' : 'bg-accent-soft text-accent')}>
                          {t.count}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>

              <span className="hidden flex-1 @3xl:block" />
              <FriendListBar active={list} onPick={setList} />

              <label className="relative w-full @3xl:w-[260px]">
                <span className="sr-only">Search friends</span>
                <Icon name="search" size={18} className="pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 text-mute" />
                <input
                  ref={searchRef}
                  type="search"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape' && filter) { e.stopPropagation(); setFilter(''); } }}
                  placeholder="Search friends..."
                  spellCheck={false}
                  className={cn(
                    'h-11 w-full rounded-[calc(14px*var(--radius-mul,1))] bg-surface-1 pr-3 pl-11 text-[15px] text-ink outline-none',
                    'shadow-[inset_0_0_0_1px_var(--line)] transition-shadow duration-[250ms] ease-[var(--ease-out)]',
                    'placeholder:text-ghost focus:shadow-[inset_0_0_0_1px_var(--accent),0_0_0_3px_var(--accent-soft)]'
                  )}
                />
              </label>
            </div>

            {/* ---- the list ---- */}
            <div id="friends-panel" role="tabpanel" aria-labelledby={'friends-tab-' + tab} key={tab} className="animate-[fade-in_250ms_ease-out] motion-reduce:animate-none">
              {isPending ? (
                <div className="flex flex-col gap-2.5" aria-busy="true" aria-label="Loading friends">
                  {[0, 1, 2, 3].map((i) => <div key={i} className="h-[92px] animate-pulse rounded-[calc(16px*var(--radius-mul,1))] bg-surface-2" />)}
                </div>
              ) : isError ? (
                <Empty
                  role="alert"
                  title="Could not load your friends"
                  note="Your friends are safe; the list just did not arrive."
                  actions={<Button kind="outline" disabled={isFetching} onClick={() => void refetch()}>{isFetching ? 'Trying…' : 'Try again'}</Button>}
                />
              ) : shown.length === 0 ? (
                filter.trim() ? (
                  <Empty
                    title={'Nobody in ' + tabLabel + ' matches “' + filter.trim() + '”'}
                    actions={<Button kind="outline" onClick={() => setFilter('')}>Clear search</Button>}
                  />
                ) : tab === 'all' ? (
                  <Empty
                    icon="friends"
                    title="No friends yet"
                    note="Add someone by their username, or meet people in a party."
                    actions={
                      <>
                        <Button kind="primary" onClick={() => setAdding(true)}>Add friend</Button>
                        <Button kind="outline" onClick={() => go('parties')}>Browse parties</Button>
                      </>
                    }
                  />
                ) : tab === 'online' ? (
                  <Empty icon="friends" title="Nobody is online right now" note="Friends show up here the moment they come online." />
                ) : tab === 'requests' ? (
                  <Empty icon="inbox" title="No friend requests" note="Requests you send and receive wait here." />
                ) : (
                  <Empty title="You have not blocked anyone" note="Blocked people cannot message or invite you." />
                )
              ) : (
                <>
                  <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
                    {paged.slice.map((f, i) => (
                      <FriendRow
                        key={f.id}
                        f={f}
                        index={i}
                        kind={kindOf(f)}
                        party={partyOf.get(f.user.id)}
                        busyPath={busyPath}
                        onAction={(a) => mutate.mutate(a)}
                      />
                    ))}
                  </ul>
                  <ShowMore rest={paged.rest} next={paged.next} noun="friend" onMore={paged.more} onAll={paged.all} className="pt-3" />
                </>
              )}
            </div>
          </section>

          {railBeside ? (
            <aside aria-label="Requests and suggestions" className="sticky top-0 flex w-[370px] flex-none flex-col gap-5">{rail}</aside>
          ) : null}
        </div>

        {!railBeside ? <div className="mt-5 grid gap-5 @container md:grid-cols-2">{rail}</div> : null}
      </div>

      {adding ? <AddFriend onClose={() => setAdding(false)} /> : null}
    </div>
  );
}

/* ------------------------------------------------------------- pieces */

const STATUS: Record<string, { dot: string; label: string; tone: string }> = {
  online: { dot: 'bg-good', label: 'Online', tone: 'text-good' },
  idle: { dot: 'bg-warn', label: 'Away', tone: 'text-warn' },
  dnd: { dot: 'bg-bad', label: 'Do not disturb', tone: 'text-bad' },
  offline: { dot: 'bg-ghost', label: 'Offline', tone: 'text-mute' },
  invisible: { dot: 'bg-ghost', label: 'Offline', tone: 'text-mute' }
};

function FriendRow({ f, index, kind, party, busyPath, onAction }: {
  f: Friendship;
  index: number;
  kind: FriendKind;
  party: Party | undefined;
  busyPath: string | undefined;
  onAction: (a: Action) => void;
}) {
  const me = useSession((s) => s.me);
  const catalog = useSession((s) => s.catalog);
  const user = f.user;
  const relation = {
    status: kind === 'accepted' ? 'accepted' : kind === 'blocked' ? 'blocked' : 'pending',
    actorId: kind === 'incoming' ? user.id : (me?.id ?? ''),
    id: f.id
  };
  const userMenu = useUserMenu(user, relation);
  const [inviting, setInviting] = useState(false);
  /*
   * No preview on hover.
   *
   * Reading down a list of friends put a panel under the pointer every time
   * it came to rest, which is most of the time - so the list you were reading
   * kept being covered by a card about whoever you had last paused over. A
   * friend row opens what it has to say when you press it, and says nothing
   * until then.
   */

  const status = STATUS[user.status] ?? STATUS.offline!;
  const up = isUp(user);
  const game = user.playing ? catalog?.games.find((g) => g.id === user.playing)?.name : null;
  const name = user.displayName;
  const busy = (path: string) => busyPath === path;

  /* First line: what they are doing. Second: the detail under it. */
  let line: React.ReactNode;
  let sub: React.ReactNode = null;
  if (kind === 'incoming') line = <span className="text-dim">Wants to be your friend{f.since ? ' · ' + sinceText(f.since) : ''}</span>;
  else if (kind === 'outgoing') line = <span className="text-dim">Request sent{f.since ? ' · ' + sinceText(f.since) : ''}</span>;
  else if (kind === 'blocked') line = <span className="text-mute">Blocked</span>;
  else if (!up) {
    line = <span className="text-mute">Offline</span>;
    if (user.lastSeen) sub = 'Last online ' + longAgo(user.lastSeen);
  } else if (game) {
    line = <span className="text-dim">Playing <span className="font-semibold text-accent">{game}</span></span>;
    sub = party ? 'In a party · ' + party.title : user.customStatus || null;
  } else if (party) {
    line = <span className="text-dim">In a party</span>;
    sub = party.title;
  } else if (user.status === 'online') {
    line = <span className="text-good">{user.customStatus ? 'Online' : 'Available to play'}</span>;
    sub = user.customStatus || 'Looking for a party';
  } else {
    line = <span className={status.tone}>{status.label}</span>;
    sub = user.customStatus || null;
  }

  return (
    <li
      {...userMenu.props}
      style={{ animationDelay: Math.min(index, 10) * 26 + 'ms' }}
      className={cn(
        ROW,
        'group flex flex-wrap items-center gap-x-4 gap-y-3 p-3 @xl:flex-nowrap @xl:px-4',
        'animate-[fade-in_280ms_ease-out_backwards] motion-reduce:animate-none'
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <button
          type="button"
          onClick={(e) => openUserCard(user, e.currentTarget)}
          aria-label={'Show ' + name + "'s profile card"}
          className="flex-none rounded-full outline-none focus-visible:shadow-[0_0_0_2px_var(--bg-1),0_0_0_4px_var(--accent)]"
        >
          <Avatar user={user} size={up || kind !== 'accepted' ? 64 : 54} />
        </button>
        <div className="min-w-0 flex-1">
          <span className="flex min-w-0 items-center gap-2">
            <UserName user={user} className={cn('truncate font-bold', up ? 'text-[18px]' : 'text-[16px]')} />
            {kind === 'accepted' ? <span aria-hidden className={cn('h-2.5 w-2.5 flex-none rounded-full', status.dot)} /> : null}
            {f.favorite ? <Icon name="star" size={14} className="flex-none text-accent" title="One of your favourites" /> : null}
          </span>
          <span className={cn('block truncate', up ? 'text-[15px]' : 'text-[13.5px]')}>
            {line}
            {!up && sub ? <span className="ml-3 text-[13px] text-mute">{sub}</span> : null}
          </span>
          {up && sub ? <span className="block truncate text-[13.5px] text-mute">{sub}</span> : null}
          {/* What they are actually up for, and what you wrote about them. */}
          {user.available || f.note ? (
            <span className="mt-1 flex min-w-0 flex-wrap items-center gap-2">
              <AvailableTag available={user.available} />
              {f.note ? (
                <span className="flex min-w-0 items-center gap-1 text-[12px] text-mute" title={f.note}>
                  <Icon name="edit" size={12} className="flex-none" />
                  <span className="truncate">{f.note}</span>
                </span>
              ) : null}
            </span>
          ) : null}
        </div>
      </div>

      <span className="ml-auto flex flex-none items-center gap-2" data-keeps-card>
        {kind === 'incoming' ? (
          <>
            <Button size="md" kind="primary" disabled={busy(`/api/friends/${f.id}/accept`)} onClick={() => onAction({ path: `/api/friends/${f.id}/accept` })}>Accept</Button>
            <Button size="md" kind="outline" disabled={busy(`/api/friends/${f.id}/decline`)} onClick={() => onAction({ path: `/api/friends/${f.id}/decline` })}>Decline</Button>
          </>
        ) : kind === 'outgoing' ? (
          <Button size="md" kind="outline" disabled={busy('DELETE /api/friends/' + f.id)} onClick={() => onAction({ path: 'DELETE /api/friends/' + f.id })}>Cancel request</Button>
        ) : kind === 'blocked' ? (
          <Button size="md" kind="outline" disabled={busy('/api/friends/unblock')} onClick={() => onAction({ path: '/api/friends/unblock', body: { userId: user.id } })}>Unblock</Button>
        ) : (
          <>
            <Button size="md" kind="outline" aria-label={'Message ' + name} icon={<Icon name="chat" size={17} />} onClick={() => go('chat/' + user.username)}>
              <span className="hidden @2xl:inline">Message</span>
            </Button>
            <Button size="md" kind="primary" aria-label={'Invite ' + name + ' to a party'} icon={<Icon name="dice" size={17} />} onClick={() => setInviting(true)}>
              <span className="hidden @2xl:inline">Invite</span>
            </Button>
          </>
        )}
        {kind === 'accepted' ? (
          <IconButton
            label={f.favorite ? 'Unpin ' + name : 'Pin ' + name + ' to the top'}
            className={cn(
              'h-[var(--h-md)] w-[var(--h-md)] rounded-[calc(12px*var(--radius-mul,1))] shadow-[inset_0_0_0_1px_var(--line)]',
              f.favorite && 'text-accent'
            )}
            onClick={() => onAction({ path: '/api/friends/' + f.id + '/favorite', body: { favorite: !f.favorite } })}
          >
            <Icon name="star" size={17} />
          </IconButton>
        ) : null}
        <IconButton
          label={'More options for ' + name}
          className="h-[var(--h-md)] w-[var(--h-md)] rounded-[calc(12px*var(--radius-mul,1))] shadow-[inset_0_0_0_1px_var(--line)]"
          onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); userMenu.openAt(Math.max(8, r.right - 214), r.bottom + 4); }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" />
          </svg>
        </IconButton>
      </span>

      {userMenu.menu}
      {inviting ? <InviteToParty user={user} onClose={() => setInviting(false)} /> : null}
    </li>
  );
}

function RailPanel({ title, count, action, children, label }: {
  title: string;
  count?: number;
  action?: React.ReactNode;
  children: React.ReactNode;
  label: string;
}) {
  return (
    <section aria-labelledby={label} className={cn(PANEL, 'p-4 animate-[fade-in_300ms_ease-out] motion-reduce:animate-none')}>
      <div className="mb-3 flex items-center gap-2.5 px-1">
        <h2 id={label} className="m-0 text-[19px] font-bold tracking-[-.01em] text-ink">{title}</h2>
        {count ? <span className="grid h-6 min-w-6 place-items-center rounded-full bg-surface-2 px-1.5 text-[12.5px] font-semibold text-dim tabular-nums">{count}</span> : null}
        <span className="flex-1" />
        {action}
      </div>
      {children}
    </section>
  );
}

function RequestsCard({ incoming, busyPath, onAction, onViewAll }: {
  incoming: Friendship[];
  busyPath: string | undefined;
  onAction: (a: Action) => void;
  onViewAll: () => void;
}) {
  return (
    <RailPanel
      title="Friend requests"
      label="friend-requests-title"
      count={incoming.length}
      action={incoming.length ? (
        <button type="button" onClick={onViewAll} className="rounded-[6px] px-1 text-[14px] font-medium text-accent outline-none hover:underline focus-visible:shadow-[0_0_0_2px_var(--accent)]">View all</button>
      ) : null}
    >
      {incoming.length ? (
        <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
          {incoming.slice(0, 3).map((f) => (
            <li key={f.id} className={cn(ROW, 'flex items-center gap-3.5 p-3')}>
              <button
                type="button"
                onClick={(e) => openUserCard(f.user, e.currentTarget)}
                aria-label={'Show ' + f.user.displayName + "'s profile card"}
                className="flex-none rounded-full outline-none focus-visible:shadow-[0_0_0_2px_var(--accent)]"
              >
                <Avatar user={f.user} size={68} />
              </button>
              <div className="min-w-0 flex-1">
                <span className="flex items-baseline gap-2">
                  <UserName user={f.user} className="min-w-0 flex-1 truncate text-[16px] font-bold" />
                  {f.since ? <span className="flex-none text-[12.5px] text-mute">{sinceText(f.since)}</span> : null}
                </span>
                <span className="block text-[13.5px] text-mute">Wants to be your friend</span>
                <span className="mt-2 flex gap-2">
                  <Button kind="primary" className="flex-1" disabled={busyPath === `/api/friends/${f.id}/accept`} onClick={() => onAction({ path: `/api/friends/${f.id}/accept` })}>Accept</Button>
                  <Button kind="outline" className="flex-1" disabled={busyPath === `/api/friends/${f.id}/decline`} onClick={() => onAction({ path: `/api/friends/${f.id}/decline` })}>Decline</Button>
                </span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="m-0 flex items-center gap-2 px-1 text-[14px] text-mute">
          <Icon name="check" size={16} className="text-good" />
          You are all caught up.
        </p>
      )}
    </RailPanel>
  );
}

/**
 * Somebody you might know.
 *
 * The line under the name is the count of friends you genuinely share, or the
 * games you both play - not a made-up percentage.
 */
function SuggestionsCard({ people, outgoing, busy, onAdd }: {
  people: Person[];
  /** Live from the friends list, so cancelling a request shows Add again. */
  outgoing: { user: { id: string } }[];
  busy: boolean;
  onAdd: (p: Person) => void;
}) {
  const catalog = useSession((s) => s.catalog);

  return (
    <RailPanel title="People you may know" label="people-you-may-know-title">
      <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
        {people.map((p) => {
          const shared = p.games
            .map((id) => catalog?.games.find((g) => g.id === id)?.name)
            .filter(Boolean)
            .slice(0, 2)
            .join(', ');
          const done = outgoing.some((o) => o.user.id === p.user.id);
          return (
            <li key={p.user.id} className={cn(ROW, 'flex items-center gap-3.5 p-3')}>
              <button
                type="button"
                onClick={(e) => openUserCard(p.user, e.currentTarget)}
                aria-label={'Show ' + p.user.displayName + "'s profile card"}
                className="flex-none rounded-full outline-none focus-visible:shadow-[0_0_0_2px_var(--accent)]"
              >
                <Avatar user={p.user} size={68} />
              </button>
              <span className="min-w-0 flex-1">
                <UserName user={p.user} className="block truncate text-[16px] font-bold" />
                <span className="block truncate text-[13.5px] text-mute">
                  {p.mutuals
                    ? p.mutuals + (p.mutuals === 1 ? ' mutual friend' : ' mutual friends')
                    : shared ? 'Both play ' + shared : 'New here'}
                </span>
              </span>
              {done ? (
                <span className="flex flex-none items-center gap-1 text-[13px] font-semibold text-good animate-[fade-in_250ms_ease-out]">
                  <Icon name="check" size={15} />
                  Sent
                </span>
              ) : (
                <IconButton
                  label={'Add ' + p.user.displayName + ' as a friend'}
                  className="h-11 w-11 flex-none rounded-[calc(12px*var(--radius-mul,1))] shadow-[inset_0_0_0_1px_var(--line)]"
                  disabled={busy}
                  onClick={() => onAdd(p)}
                >
                  <Icon name="addFriend" size={18} />
                </IconButton>
              )}
            </li>
          );
        })}
      </ul>
    </RailPanel>
  );
}

/**
 * Add a friend.
 *
 * A panel on the right of the page you are already reading, not a box over
 * the middle of it. Typing a name searches everyone on Pugo and lists them
 * with their picture, so you add the person you meant rather than guessing at
 * the spelling of a username; each row says where you already stand with them
 * - friends, requested, or waiting on you - and the button does the one thing
 * left to do. An exact username you typed still works even when the directory
 * has nothing to show, because somebody can opt out of being listed.
 */
function AddFriend({ onClose }: { onClose: () => void }) {
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const queryClient = useQueryClient();
  const toast = useToast();
  const me = useSession((s) => s.me);

  /* One search a keystroke would be one request a keystroke. */
  useEffect(() => {
    const id = window.setTimeout(() => setQ(typed.trim().replace(/^@/, '')), 220);
    return () => window.clearTimeout(id);
  }, [typed]);

  const term = q.toLowerCase();
  const search = useQuery({
    queryKey: ['player-search', term],
    queryFn: () => api.get<{ players: PublicUser[]; total: number }>('/api/players?q=' + encodeURIComponent(term)),
    enabled: term.length >= 2,
    staleTime: 20_000
  });

  /* Where you already stand with each person, so a row never offers to send a
     request that would only be refused. */
  const friends = useQuery({ queryKey: ['friends'], queryFn: endpoints.friends });
  const standing = useMemo(() => {
    const map = new Map<string, 'friend' | 'outgoing' | 'incoming' | 'blocked'>();
    for (const f of friends.data?.friends ?? []) map.set(f.user.id, 'friend');
    for (const f of friends.data?.outgoing ?? []) map.set(f.user.id, 'outgoing');
    for (const f of friends.data?.incoming ?? []) map.set(f.user.id, 'incoming');
    for (const f of friends.data?.blocked ?? []) map.set(f.user.id, 'blocked');
    return map;
  }, [friends.data]);

  const results = (search.data?.players ?? []).filter((u) => u.id !== me?.id);
  /* Ten at a time. Twenty-five rows of avatars for a two-letter search is
     most of the wait in this dialog, and nobody reads past the first few. */
  const found = usePaged(results, term);

  async function request(username: string, id?: string) {
    if (busy) return;
    setBusy(id || username);
    setError('');
    try {
      const res = await api.post<{ status: string }>('/api/friends/request', { username: username.toLowerCase() });
      toast(res.status === 'accepted' ? 'You are now friends with ' + username : 'Friend request sent to ' + username, 'good');
      /* The friends list is the answer to "have I asked this person"; it
         comes back refreshed and the rows read it. Keeping a second copy of
         it here is what made a cancelled request still say Sent. */
      await queryClient.invalidateQueries({ queryKey: ['friends'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy('');
    }
  }

  async function accept(userId: string) {
    const row = (friends.data?.incoming ?? []).find((x) => x.user.id === userId);
    if (!row || busy) return;
    setBusy(userId);
    try {
      await api.post('/api/friends/' + row.id + '/accept', {});
      toast('You are now friends', 'good');
      void queryClient.invalidateQueries({ queryKey: ['friends'] });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy('');
    }
  }

  /* Somebody can keep themselves out of the directory, and a brand new name
     will not be in it either - the exact thing you typed is still sendable. */
  const exact = q.length >= 2 && /^[a-zA-Z0-9_.-]+$/.test(q);
  const listedExact = results.some((u) => u.username === term);

  return (
    <Modal
      side
      title="Add a friend"
      subtitle="Search for anyone on Pugo, or type a username you already know."
      onClose={onClose}
      footer={<Button kind="plain" onClick={onClose}>Done</Button>}
    >
      <div className="flex flex-col gap-3">
        <label className="relative block">
          <span className="sr-only">Search for a player</span>
          <Icon name="search" size={18} className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-mute" />
          <input
            autoFocus
            type="search"
            value={typed}
            onChange={(e) => { setTyped(e.target.value); setError(''); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && exact && !results.length) { e.preventDefault(); void request(q); }
              if (e.key === 'Escape' && typed) { e.stopPropagation(); setTyped(''); }
            }}
            placeholder="Name or username…"
            spellCheck={false}
            autoComplete="off"
            aria-invalid={!!error}
            className={cn(
              'h-12 w-full rounded-[calc(14px*var(--radius-mul,1))] bg-surface-1 pr-3 pl-11 text-[15px] text-ink outline-none',
              'shadow-[inset_0_0_0_1px_var(--line)] transition-shadow duration-[250ms] ease-[var(--ease-out)]',
              'placeholder:text-ghost focus:shadow-[inset_0_0_0_1px_var(--accent),0_0_0_3px_var(--accent-soft)]'
            )}
          />
          {search.isFetching ? (
            <span aria-hidden className="absolute top-1/2 right-4 h-4 w-4 -translate-y-1/2 animate-spin rounded-full border-2 border-line border-t-accent" />
          ) : null}
        </label>

        {error ? <p role="alert" className="m-0 text-[13.5px] text-bad">{error}</p> : null}

        {q.length < 2 ? (
          <p className="m-0 px-1 py-6 text-center text-[13.5px] text-mute">
            Start typing a name. Everyone who matches appears here with an Add button.
          </p>
        ) : results.length === 0 && !search.isFetching ? (
          <div className="flex flex-col items-center gap-3 px-1 py-6 text-center">
            <p className="m-0 text-[13.5px] text-mute">Nobody listed matches “{q}”.</p>
            {exact ? (
              <Button kind="outline" disabled={!!busy} onClick={() => void request(q)}>
                {busy ? 'Sending…' : 'Send a request to @' + q}
              </Button>
            ) : null}
          </div>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
            {found.slice.map((u) => {
              const where = standing.get(u.id);
              const waiting = busy === u.id;
              return (
                <li
                  key={u.id}
                  className="flex items-center gap-3 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-1 p-2 shadow-[inset_0_0_0_1px_var(--line-soft)]"
                >
                  <button type="button" onClick={(e) => openUserCard(u, e.currentTarget)} className="flex-none" aria-label={'Show profile card of ' + u.displayName}>
                    <Avatar user={u} size={38} presence />
                  </button>
                  <span className="min-w-0 flex-1">
                    <UserName user={u} className="block truncate text-[14px] font-semibold" />
                    <span className="block truncate text-[12px] text-mute">@{u.username}</span>
                  </span>

                  {where === 'friend' ? (
                    <span className="flex-none rounded-full bg-accent-soft px-2.5 py-1 text-[12px] font-semibold text-accent">Friends</span>
                  ) : where === 'blocked' ? (
                    <span className="flex-none rounded-full bg-surface-3 px-2.5 py-1 text-[12px] font-semibold text-mute">Blocked</span>
                  ) : where === 'outgoing' ? (
                    <span className="flex-none rounded-full bg-surface-3 px-2.5 py-1 text-[12px] font-semibold text-dim">Requested</span>
                  ) : where === 'incoming' ? (
                    <Button size="sm" kind="primary" disabled={waiting} onClick={() => void accept(u.id)}>
                      {waiting ? '…' : 'Accept'}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      kind="outline"
                      disabled={waiting || !!busy}
                      icon={<Icon name="addFriend" size={15} />}
                      onClick={() => void request(u.username, u.id)}
                    >
                      {waiting ? 'Sending…' : 'Add'}
                    </Button>
                  )}
                </li>
              );
            })}

            {exact && !listedExact ? (
              <li className="mt-1 flex items-center gap-3 rounded-[calc(14px*var(--radius-mul,1))] border border-dashed border-line p-2.5">
                <span className="min-w-0 flex-1 text-[12.5px] text-mute">Not the person you meant? Send to the exact username.</span>
                <Button size="sm" kind="plain" disabled={!!busy} onClick={() => void request(q)}>@{q}</Button>
              </li>
            ) : null}
            <li><ShowMore rest={found.rest} next={found.next} noun="result" onMore={found.more} onAll={found.all} /></li>
          </ul>
        )}
      </div>
    </Modal>
  );
}

/** One party invite in the side column. */
export function InviteRow({ invite }: { invite: { id: string; party: Party; from: PublicUser | null } }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const catalog = useSession((s) => s.catalog);
  const game = catalog?.games.find((g) => g.id === invite.party?.gameId);

  async function act(accept: boolean) {
    try {
      await api.post('/api/invites/' + invite.id + (accept ? '/accept' : '/decline'), {});
      toast(accept ? 'Joined the party' : 'Invite declined', 'good');
      await queryClient.invalidateQueries({ queryKey: ['invites'] });
      await queryClient.invalidateQueries({ queryKey: ['parties'] });
      if (accept && invite.party) go('party/' + invite.party.id);
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  return (
    <div className="flex items-center gap-2.5 rounded-[var(--radius)] border border-line-soft bg-surface-1 p-2">
      {invite.from ? <Avatar user={invite.from} size={30} presence /> : null}
      <div className="min-w-0 flex-1">
        <span className="block truncate text-[12.5px] font-semibold">
          {invite.from?.displayName ?? 'Someone'}
        </span>
        <span className="block truncate text-[11px] text-mute">
          {game?.name ?? invite.party?.title ?? 'a party'}
        </span>
      </div>
      <IconButton label="Decline" onClick={() => void act(false)}>
        <Icons.Close size={13} />
      </IconButton>
      <IconButton label="Accept" onClick={() => void act(true)}>
        <Icons.CheckCircle size={14} className="text-good" />
      </IconButton>
    </div>
  );
}

/** One incoming friend request in the side column. */
export function RequestRow({ f, onAccept, onDecline }: {
  f: Friendship;
  onAccept: () => void;
  onDecline: () => void;
}) {
  return (
    <div className="flex items-center gap-2.5 rounded-[var(--radius)] border border-line-soft bg-surface-1 p-2">
      <button type="button" onClick={(e) => openUserCard(f.user, e.currentTarget)} className="flex-none">
        <Avatar user={f.user} size={30} presence />
      </button>
      <div className="min-w-0 flex-1">
        <UserName user={f.user} className="block truncate text-[12.5px] font-semibold" />
        <span className="block text-[11px] text-mute">Wants to be friends</span>
      </div>
      <IconButton label="Decline" onClick={onDecline}>
        <Icons.Close size={13} />
      </IconButton>
      <IconButton label="Accept" onClick={onAccept}>
        <Icons.CheckCircle size={14} className="text-good" />
      </IconButton>
    </div>
  );
}
